import { Box } from "lucide-react";
import type { TLShape } from "@tldraw/tlschema";
import type { Editor } from "./canvas";
import type { WorkspaceModel } from "./useWorkspace";
import { scaleSource, sourceMeters } from "./source-scale";
import { modelAssetInfo } from "./model-assets";

/**
 * One click from a drawing to a 3D model. The button asks for a scale when
 * the drawing has none (estimating size is the one thing it must not guess),
 * then hands the agent panel a fixed request that builds without questions.
 */
export const MAKE_3D_EVENT = "calque:make3d";

/** Ask the agent panel to build a 3D model of this drawing. */
export function requestMake3d(shapeId: string) {
  window.dispatchEvent(new CustomEvent<{ shapeId: string }>(MAKE_3D_EVENT, { detail: { shapeId } }));
}

/** Plan drawings: a sheet with a drawing or contents, or a plain image (not a 3D model or tracing snapshot). */
export function isDrawing(editor: Editor, shape: TLShape | undefined): boolean {
  if (!shape || shape.isLocked) return false;
  if (shape.type === "sheet")
    return !!(shape.props as { preview?: string }).preview || editor.getSortedChildIdsForParent(shape.id).length > 0;
  if (shape.type !== "image" || shape.meta.traceSnapshot) return false;
  const assetId = (shape.props as { assetId?: string | null }).assetId;
  const asset = assetId ? editor.getAsset(assetId as never) : undefined;
  return !!asset && !modelAssetInfo(asset);
}

/** Start Make 3D: straight away when the drawing has a scale, else ask for one first. */
export function startMake3d(model: WorkspaceModel, shape: TLShape) {
  const source = scaleSource(model.editor, shape);
  if (!source) return;
  if (sourceMeters(model.editor, source) > 0) requestMake3d(source.id);
  else model.requestScale(source, "make3d");
}

export function MakeThreeDButton({ model, shape }: { model: WorkspaceModel; shape: TLShape | undefined }) {
  if (!shape || !model.canEdit || !model.user.features?.agents || !isDrawing(model.editor, shape)) return null;
  return (
    <button
      className="make-3d-button"
      title="Build a 3D model of this drawing beside it"
      onClick={() => startMake3d(model, shape)}
    >
      <Box size={16} /> Make 3D
    </button>
  );
}
